const { jQuery: $ } = window;
import Utils from "./utils.class";
import Cookies from 'js-cookie';

/**
 * Bridges the CMP's marketing consent to Pardot's tracking opt-in.
 *
 * Pardot's tracker (pd.js, loaded via GTM) only tracks when the partech.com pi_opt_in{N} cookie isn't "false", and
 * the form handlers and iframe forms read a separate go.partech.com cookie that only Pardot's /analytics response can
 * set. Pardot has no API for this, so we write the local cookie and re-run piTracker() to tell go.partech.com.
 */
export default class PardotConsent {
    static #OPT_OUT_PENDING = "kmdg_pi_opt_out_pending";
    static #WAIT_LIMIT = 15000;
    #accountID;

    #waiting = false;

    /**
     * @param {int} accountID
     * @param {CMP} cmp
     */
    constructor(accountID, cmp) {
        Utils.log("Registered: Pardot Consent");
        this.#accountID = accountID;

        $(window).on(cmp.events.consent.marketing, () => this.#sync(true));
        $(window).on(cmp.events.denied.marketing, () => this.#sync(false));
    }

    #sync(optIn) {
        const current = Cookies.get(this.#cookieName);
        const desired = optIn ? "true" : "false";

        if (current !== desired) {
            Utils.log(`[Pardot] Tracking opt-in: ${current || "(unset)"} -> ${desired}`);

            Cookies.set(this.#cookieName, desired, {
                expires: 365,
                path: "/",
                sameSite: "Lax",
                secure: location.protocol === "https:"
            });

            if (optIn) {
                // If pd.js hasn't loaded yet it will read the new cookie and send pi_opt_in=true by itself.
                if (this.#trackerLoaded) {
                    this.#notify();
                }

                localStorage.removeItem(PardotConsent.#OPT_OUT_PENDING);
                return;
            }

            // Only an opted-in visitor can have a "true" cookie on go.partech.com, so don't send a decline to Pardot
            // for visitors who never consented.
            if (current === "true") {
                localStorage.setItem(PardotConsent.#OPT_OUT_PENDING, "1");
            }
        }

        if (!optIn && localStorage.getItem(PardotConsent.#OPT_OUT_PENDING)) {
            this.#whenTrackerLoaded(() => {
                this.#notify();
                localStorage.removeItem(PardotConsent.#OPT_OUT_PENDING);
            });
        }
    }

    /**
     * Re-sends the tracking request so go.partech.com updates its own opt-in cookie. With an opt-out cookie pd.js only
     * sends when notify_pi is set and a title is present (the path Pardot's own consent banner uses).
     */
    #notify() {
        const { pi } = window;

        Utils.log("[Pardot] Notifying go.partech.com of the opt-in change.");

        pi.tracker.title = document.title;
        pi.tracker.notify_pi = true;
        window.piTracker();

        // pd.js sends every pi.tracker property, so don't leave the flag on later requests.
        delete pi.tracker.notify_pi;
    }

    #whenTrackerLoaded(callback) {
        if (this.#trackerLoaded) {
            callback();
            return;
        }

        if (this.#waiting) {
            return;
        }

        this.#waiting = true;
        const started = Date.now();

        const watcher = setInterval(() => {
            if (this.#trackerLoaded) {
                clearInterval(watcher);
                this.#waiting = false;
                callback();
            } else if (Date.now() - started > PardotConsent.#WAIT_LIMIT) {
                // Blocked or not loaded on this page; the pending flag retries on the next page view.
                clearInterval(watcher);
                this.#waiting = false;
                Utils.log("[Pardot] Tracker not loaded, opt-out notification deferred.");
            }
        }, 250);
    }

    get #trackerLoaded() {
        return typeof window.piTracker === "function" && !!window.pi?.tracker;
    }

    /**
     * pd.js names its cookies after piAId - 1000 (the account ID).
     */
    get #cookieName() {
        const accountId = typeof window.piAId !== "undefined" ? window.piAId - 1000 : this.#accountID;

        return `pi_opt_in${accountId}`;
    }
}
